"use client";

import React, { useState, useMemo } from "react";
import { Search, FileText, Download, SlidersHorizontal, ShieldCheck, ArrowRight, Info, CircleCheck, CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
interface SpecRow {
  meshCount: string;
  wireDiaMm: string;
  wireDiaInch: string;
  apertureMicron: number;
  openAreaPercent: string;
  standardAlloys: string;
  typicalApp: string;
}
const PLAIN_WEAVE_SPECS: SpecRow[] = [{
  meshCount: "10 x 10",
  wireDiaMm: "0.50 mm",
  wireDiaInch: '0.020"',
  apertureMicron: 2040,
  openAreaPercent: "64.5%",
  standardAlloys: "304L / 316L",
  typicalApp: "Coarse Screening / Strainers"
}, {
  meshCount: "20 x 20",
  wireDiaMm: "0.40 mm",
  wireDiaInch: '0.016"',
  apertureMicron: 870,
  openAreaPercent: "46.9%",
  standardAlloys: "304L / 316L / 316Ti",
  typicalApp: "Polymer Pre-Filtration"
}, {
  meshCount: "40 x 40",
  wireDiaMm: "0.25 mm",
  wireDiaInch: '0.010"',
  apertureMicron: 385,
  openAreaPercent: "36.8%",
  standardAlloys: "304L / 316L / Inconel",
  typicalApp: "Chemical Process Screens"
}, {
  meshCount: "80 x 80",
  wireDiaMm: "0.14 mm",
  wireDiaInch: '0.0055"',
  apertureMicron: 178,
  openAreaPercent: "31.4%",
  standardAlloys: "316L / Monel 400",
  typicalApp: "Hydraulic Fluid Filtration"
}, {
  meshCount: "100 x 100",
  wireDiaMm: "0.10 mm",
  wireDiaInch: '0.0040"',
  apertureMicron: 154,
  openAreaPercent: "36.8%",
  standardAlloys: "316L / Hastelloy C276",
  typicalApp: "Fine Particle Separation"
}, {
  meshCount: "200 x 200",
  wireDiaMm: "0.05 mm",
  wireDiaInch: '0.0021"',
  apertureMicron: 77,
  openAreaPercent: "36.8%",
  standardAlloys: "316L / 316Ti",
  typicalApp: "Gas Liquid Separation"
}, {
  meshCount: "325 x 325",
  wireDiaMm: "0.035 mm",
  wireDiaInch: '0.0014"',
  apertureMicron: 43,
  openAreaPercent: "30.3%",
  standardAlloys: "316L High Yield",
  typicalApp: "Precision Extrusion Filtration"
}];
const DUTCH_WEAVE_SPECS: SpecRow[] = [{
  meshCount: "24 x 110 (Plain Dutch)",
  wireDiaMm: "0.35 x 0.25 mm",
  wireDiaInch: '0.014" x 0.010"',
  apertureMicron: 120,
  openAreaPercent: "N/A (3D Flow)",
  standardAlloys: "316L / 304L",
  typicalApp: "High Pressure Liquid Strainers"
}, {
  meshCount: "80 x 700 (Twill Dutch)",
  wireDiaMm: "0.10 x 0.07 mm",
  wireDiaInch: '0.004" x 0.0028"',
  apertureMicron: 35,
  openAreaPercent: "N/A (3D Flow)",
  standardAlloys: "316L / Inconel 600",
  typicalApp: "Polymer Melt Filtration"
}, {
  meshCount: "165 x 1400 (Twill Dutch)",
  wireDiaMm: "0.07 x 0.04 mm",
  wireDiaInch: '0.0028" x 0.0016"',
  apertureMicron: 18,
  openAreaPercent: "N/A (3D Flow)",
  standardAlloys: "316L Vacuum Melt",
  typicalApp: "Aerospace Fuel System Mesh"
}, {
  meshCount: "200 x 1400 (Twill Dutch)",
  wireDiaMm: "0.07 x 0.04 mm",
  wireDiaInch: '0.0028" x 0.0016"',
  apertureMicron: 12,
  openAreaPercent: "N/A (3D Flow)",
  standardAlloys: "316L / Hastelloy",
  typicalApp: "Pharmaceutical Liquid Clarification"
}, {
  meshCount: "325 x 2300 (Twill Dutch)",
  wireDiaMm: "0.038 x 0.025 mm",
  wireDiaInch: '0.0015" x 0.0010"',
  apertureMicron: 5,
  openAreaPercent: "N/A (3D Flow)",
  standardAlloys: "316L Ultra-Clean",
  typicalApp: "Critical Sub-Micron Filtration"
}];
const SINTERED_MESH_SPECS: SpecRow[] = [{
  meshCount: "5-Layer Standard Sintered",
  wireDiaMm: "Laminate (1.70mm thick)",
  wireDiaInch: '0.067" Plate',
  apertureMicron: 100,
  openAreaPercent: "Custom Flow",
  standardAlloys: "316L Sintered",
  typicalApp: "Fluidized Beds & Gas Sparging"
}, {
  meshCount: "5-Layer Fine Sintered",
  wireDiaMm: "Laminate (1.70mm thick)",
  wireDiaInch: '0.067" Plate',
  apertureMicron: 20,
  openAreaPercent: "Custom Flow",
  standardAlloys: "316L Sintered",
  typicalApp: "High Temp Catalyst Recovery"
}, {
  meshCount: "5-Layer Precision Sintered",
  wireDiaMm: "Laminate (1.70mm thick)",
  wireDiaInch: '0.067" Plate',
  apertureMicron: 5,
  openAreaPercent: "Custom Flow",
  standardAlloys: "316L / Inconel 600",
  typicalApp: "High Differential Pressure Elements"
}, {
  meshCount: "3-Layer Heavy Duty Sintered",
  wireDiaMm: "Laminate (2.50mm thick)",
  wireDiaInch: '0.098" Plate',
  apertureMicron: 50,
  openAreaPercent: "Custom Flow",
  standardAlloys: "316L Structural",
  typicalApp: "Nutsche Filter Dryer Plates"
}];
function TechnicalDocumentsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {[...Array(3)].map((_, i) => <div key={i} className="h-44 rounded-lg bg-muted/40 border border-border" />)}
      </div>;
  }
  if (error) {
    return <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm">
        Unable to retrieve official datasheets. Technical support is available on request.
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="p-6 text-center text-muted-foreground border border-border rounded-lg bg-card">
        No technical specification documents uploaded yet.
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
      {posts.map((post, i) => {
      const docCode = post.customFields?.doc_code || "SPEC-ENG";
      const summary = post.customFields?.summary || post.excerpt || "Engineering specification sheet for industrial stainless wire mesh.";
      const formatLabel = post.customFields?.format_label || "PDF Specs";
      const specsHighlight = post.customFields?.specs_highlight || "316L Stainless Steel / ISO 9044";
      const pageCount = post.customFields?.page_count;
      return <Card key={post.id} data-index={i} className="flex flex-col justify-between rounded-lg border border-border bg-card">
            <CardHeader className="p-5 pb-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <Badge variant="outline" className="font-mono mf-caption uppercase tracking-wider bg-muted text-foreground">
                  {docCode}
                </Badge>
                <span className="text-[13px] text-muted-foreground font-medium flex items-center gap-1">
                  <FileText className="size-3.5 text-primary shrink-0" />
                  {formatLabel} {pageCount ? `(${pageCount} p.)` : ""}
                </span>
              </div>
              <CardTitle className="mf-h3 leading-tight text-card-foreground">
                {post.title}
              </CardTitle>
              <CardDescription className="line-clamp-2 text-[13px] text-muted-foreground mt-1">
                {summary}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-5 py-2">
              <div className="text-[13px] font-mono bg-muted/60 px-2.5 py-1.5 rounded-md border border-border text-foreground/90 flex items-center gap-1.5">
                <ShieldCheck className="size-3.5 text-primary shrink-0" />
                <span className="truncate">{specsHighlight}</span>
              </div>
            </CardContent>
            <CardFooter className="p-5 pt-3 border-t border-border/50">
              <Button asChild variant="outline" className="w-full justify-between hover:bg-accent">
                <Link to={post.link || "/DatasheetsPage"}>
                  <span className="text-[13px] font-medium">Download Datasheet</span>
                  <Download className="size-3.5 text-primary" />
                </Link>
              </Button>
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function Specifications() {
  const {
    lang
  } = useLanguage();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("plain");
  const activeDataset = useMemo(() => {
    switch (activeTab) {
      case "dutch":
        return DUTCH_WEAVE_SPECS;
      case "sintered":
        return SINTERED_MESH_SPECS;
      case "plain":
      default:
        return PLAIN_WEAVE_SPECS;
    }
  }, [activeTab]);
  const filteredSpecs = useMemo(() => {
    if (!searchTerm.trim()) return activeDataset;
    const term = searchTerm.toLowerCase();
    return activeDataset.filter(item => item.meshCount.toLowerCase().includes(term) || item.apertureMicron.toString().includes(term) || item.wireDiaMm.toLowerCase().includes(term) || item.standardAlloys.toLowerCase().includes(term) || item.typicalApp.toLowerCase().includes(term));
  }, [activeDataset, searchTerm]);
  const wpQuery = useMemo(() => {
    const params = {
      post_type: "technical_document",
      per_page: 3,
      order: "desc",
      orderby: "date"
    };
    return new WP_Query(params);
  }, []);
  return <section id="specifications" data-nav="light" className="relative bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-10 border-b border-border">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-border bg-muted text-primary mf-caption font-semibold uppercase tracking-wider mb-3">
              <ShieldCheck className="size-3.5" aria-hidden="true" />
              Technical Engineering Data
            </div>
            <h2 className="mf-h2 text-foreground text-balance">
              Wire mesh specifications & dimensional standards
            </h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">
              Standard aperture dimensions, wire diameters, micron ratings, and open area percentages manufactured in accordance with ASTM E2016 and ISO 9044 tolerances.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button asChild variant="outline" className="gap-2">
              <Link to="/CalculationGuidesPage">
                <SlidersHorizontal className="size-4 text-primary" aria-hidden="true" />
                Aperture Calculators
              </Link>
            </Button>
            <Button asChild variant="outline" className="gap-2">
              <Link to="/TechnicalChartsPage">
                <FileText className="size-4 text-primary" aria-hidden="true" />
                Technical Charts
              </Link>
            </Button>
            <Button asChild variant="outline" className="gap-2">
              <Link to="/StandardsAndInspectionPage">
                <ShieldCheck className="size-4 text-primary" aria-hidden="true" />
                {pick(lang, {
                en: "Standards & Inspection",
                zh: "标准与检验规程",
                es: "Normas e inspección",
                de: "Normen & Inspektion",
                fr: "Normes et contrôle",
                pt: "Normas e inspeção"
              })}
              </Link>
            </Button>
          </div>
        </div>

        {/* Specification Family Navigation Cards */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[{
          href: "/WireMeshSpecificationsPage",
          badge: "ASTM E2016",
          title: {
            en: "Wire Mesh Specifications",
            zh: "金属丝网规格",
            es: "Especificaciones de malla de alambre",
            de: "Drahtgewebe-Spezifikationen",
            fr: "Spécifications des toiles métalliques",
            pt: "Especificações de malha de arame"
          },
          desc: {
            en: "Square weave mesh count, wire gauges, and dimensional tolerances.",
            zh: "方孔丝网目数、丝径规格及尺寸公差要求。",
            es: "Conteo de malla tejida cuadrada, calibres de alambre y tolerancias dimensionales.",
            de: "Quadratgewebe-Maschenzahlen, Drahtstärken und Maßtoleranzen.",
            fr: "Nombre de mailles carrées, jauges de fil et tolérances dimensionnelles.",
            pt: "Contagem de malha quadrada, bitolas de arame e tolerâncias dimensionais."
          }
        }, {
          href: "/FilterMeshSpecificationsPage",
          badge: "ASTM E2814",
          title: {
            en: "Filter Mesh Specifications",
            zh: "过滤网规格",
            es: "Especificaciones de malla filtrante",
            de: "Filtergewebe-Spezifikationen",
            fr: "Spécifications des toiles filtrantes",
            pt: "Especificações de malha filtrante"
          },
          desc: {
            en: "Plain, twilled, and reverse Dutch weave specifications and retention ratings.",
            zh: "平纹、斜纹与反向荷兰编织密纹网规格及截留精度。",
            es: "Especificaciones y retención de tejidos Dutch lisos, cruzados e invertidos.",
            de: "Spezifikationen für glatte, Köper- und Panzer-Tressengewebe.",
            fr: "Spécifications des tissages hollandais unis, sergés et inversés.",
            pt: "Especificações de tramas holandesas lisas, sarja e invertidas."
          }
        }, {
          href: "/WedgeWireSpecificationsPage",
          badge: "Profile Wire",
          title: {
            en: "Wedge Wire Specifications",
            zh: "楔形丝网规格",
            es: "Especificaciones de alambre en cuña",
            de: "Spaltsieb-Spezifikationen",
            fr: "Spécifications des fils profilés",
            pt: "Especificações de arame em cunha"
          },
          desc: {
            en: "Slot opening dimensions, profile wire geometries, and support rod layouts.",
            zh: "连续缝隙开孔尺寸、楔形丝截面几何与支撑杆结构。",
            es: "Dimensiones de ranura continua, geometrías de alambre y varillas de soporte.",
            de: "Spaltweitenabmessungen, Profildrahtgeometrien und Tragstabprofile.",
            fr: "Largeurs de fente, géométries des fils profilés et barres de support.",
            pt: "Dimensões de abertura de fenda, geometrias de arame e hastes de suporte."
          }
        }, {
          href: "/StandardsAndInspectionPage",
          badge: "ISO 9044 / ASTM",
          title: {
            en: "Standards & Inspection",
            zh: "标准与检验规程",
            es: "Normas e inspección",
            de: "Normen & Inspektion",
            fr: "Normes et contrôle",
            pt: "Normas e inspeção"
          },
          desc: {
            en: "Dimensional tolerances, inspection criteria, and alloy verification protocols.",
            zh: "尺寸公差限值、质量外观验收及材质验证流程。",
            es: "Tolerancias dimensionales, criterios de inspección y verificación de aleación.",
            de: "Maßtoleranzen, Prüfkriterien und Legierungsprüfverfahren.",
            fr: "Tolérances dimensionnelles, critères d'inspection et protocoles d'alliage.",
            pt: "Tolerâncias dimensionais, critérios de inspeção e verificação de ligas."
          }
        }].map(item => <Link key={item.href} to={item.href} className="group flex flex-col justify-between rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
              <div className="space-y-2">
                <Badge variant="outline" className="font-mono text-xs uppercase bg-muted text-foreground w-fit">
                  {item.badge}
                </Badge>
                <div className="font-semibold text-sm text-foreground transition-colors group-hover:text-primary">
                  {pick(lang, item.title)}
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {pick(lang, item.desc)}
                </p>
              </div>
              <div className="pt-3 mt-2 border-t border-border/60 flex items-center gap-1 text-xs font-medium text-primary">
                <span>
                  {pick(lang, {
                en: "View Specifications",
                zh: "查看详细规格",
                es: "Ver especificaciones",
                de: "Spezifikationen ansehen",
                fr: "Consulter les spécifications",
                pt: "Ver especificações"
              })}
                </span>
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </div>
            </Link>)}
        </div>

        {/* Specification Table Module */}
        <div className="mt-10">
          <Tabs defaultValue="plain" onValueChange={setActiveTab} className="w-full">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <TabsList className="bg-muted p-1 border border-border">
                <TabsTrigger value="plain" className="text-[13px] sm:text-sm font-medium">
                  Plain Square Mesh
                </TabsTrigger>
                <TabsTrigger value="dutch" className="text-[13px] sm:text-sm font-medium">
                  Dutch Weave Cloth
                </TabsTrigger>
                <TabsTrigger value="sintered" className="text-[13px] sm:text-sm font-medium">
                  Sintered Laminates
                </TabsTrigger>
              </TabsList>

              {/* Search Filter */}
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input type="search" placeholder="Filter mesh, micron, alloy..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 text-[13px] sm:text-sm bg-card border-border" />
              </div>
            </div>

            <TooltipProvider>
              <TabsContent value="plain" className="m-0">
                <div className="rounded-lg border border-border bg-card overflow-hidden overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-muted/60">
                      <TableRow className="border-border">
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider py-3.5">
                          Mesh Count
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          Wire Dia. (Metric)
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          Wire Dia. (Imperial)
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          <div className="flex items-center gap-1">
                            Aperture / Micron
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="size-3.5 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                Nominal opening size measured in micrometers (1 μm = 0.001 mm).
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          Open Area
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          Standard Alloys
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider text-right pr-4">
                          Primary Industrial Use
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSpecs.length > 0 ? filteredSpecs.map((row, idx) => <TableRow key={idx} data-index={idx} className="border-border hover:bg-muted/40">
                            <TableCell className="font-mono font-medium text-foreground py-3 text-sm">
                              {row.meshCount}
                            </TableCell>
                            <TableCell className="font-mono text-[13px] text-muted-foreground">
                              {row.wireDiaMm}
                            </TableCell>
                            <TableCell className="font-mono text-[13px] text-muted-foreground">
                              {row.wireDiaInch}
                            </TableCell>
                            <TableCell className="font-mono text-sm font-semibold text-primary">
                              {row.apertureMicron} μm
                            </TableCell>
                            <TableCell className="font-mono text-[13px] text-foreground/80">
                              {row.openAreaPercent}
                            </TableCell>
                            <TableCell className="text-[13px] font-medium text-foreground">
                              {row.standardAlloys}
                            </TableCell>
                            <TableCell className="text-[13px] text-muted-foreground text-right pr-4">
                              {row.typicalApp}
                            </TableCell>
                          </TableRow>) : <TableRow>
                          <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                            No specifications matching "{searchTerm}". Try searching for another micron or mesh value.
                          </TableCell>
                        </TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="dutch" className="m-0">
                <div className="rounded-lg border border-border bg-card overflow-hidden overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-muted/60">
                      <TableRow className="border-border">
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider py-3.5">
                          Dutch Weave Specification
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          Warp x Weft Wire Dia.
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          Imperial Wire Gauge
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          Nominal Retention
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          Flow Dynamics
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          Alloy Availability
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider text-right pr-4">
                          Target Application
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSpecs.length > 0 ? filteredSpecs.map((row, idx) => <TableRow key={idx} data-index={idx} className="border-border hover:bg-muted/40">
                            <TableCell className="font-mono font-medium text-foreground py-3 text-sm">
                              {row.meshCount}
                            </TableCell>
                            <TableCell className="font-mono text-[13px] text-muted-foreground">
                              {row.wireDiaMm}
                            </TableCell>
                            <TableCell className="font-mono text-[13px] text-muted-foreground">
                              {row.wireDiaInch}
                            </TableCell>
                            <TableCell className="font-mono text-sm font-semibold text-primary">
                              {row.apertureMicron} μm
                            </TableCell>
                            <TableCell className="font-mono text-[13px] text-foreground/80">
                              {row.openAreaPercent}
                            </TableCell>
                            <TableCell className="text-[13px] font-medium text-foreground">
                              {row.standardAlloys}
                            </TableCell>
                            <TableCell className="text-[13px] text-muted-foreground text-right pr-4">
                              {row.typicalApp}
                            </TableCell>
                          </TableRow>) : <TableRow>
                          <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                            No specification entries match "{searchTerm}".
                          </TableCell>
                        </TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="sintered" className="m-0">
                <div className="rounded-lg border border-border bg-card overflow-hidden overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-muted/60">
                      <TableRow className="border-border">
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider py-3.5">
                          Sintered Structure
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          Plate Thickness (mm)
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          Plate Thickness (Inch)
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          Micron Rating
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          Flow Characteristics
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider">
                          Base Metallurgy
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-[13px] uppercase tracking-wider text-right pr-4">
                          Primary Process
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSpecs.length > 0 ? filteredSpecs.map((row, idx) => <TableRow key={idx} data-index={idx} className="border-border hover:bg-muted/40">
                            <TableCell className="font-mono font-medium text-foreground py-3 text-sm">
                              {row.meshCount}
                            </TableCell>
                            <TableCell className="font-mono text-[13px] text-muted-foreground">
                              {row.wireDiaMm}
                            </TableCell>
                            <TableCell className="font-mono text-[13px] text-muted-foreground">
                              {row.wireDiaInch}
                            </TableCell>
                            <TableCell className="font-mono text-sm font-semibold text-primary">
                              {row.apertureMicron} μm
                            </TableCell>
                            <TableCell className="font-mono text-[13px] text-foreground/80">
                              {row.openAreaPercent}
                            </TableCell>
                            <TableCell className="text-[13px] font-medium text-foreground">
                              {row.standardAlloys}
                            </TableCell>
                            <TableCell className="text-[13px] text-muted-foreground text-right pr-4">
                              {row.typicalApp}
                            </TableCell>
                          </TableRow>) : <TableRow>
                          <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                            No sintered spec entries match "{searchTerm}".
                          </TableCell>
                        </TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </TooltipProvider>
          </Tabs>

          <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 mf-caption text-muted-foreground px-1">
            <span className="flex items-center gap-1.5">
              <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
              All mesh aperture tolerances inspected to ASTM E2016 standards.
            </span>
            <div className="flex flex-wrap items-center gap-3">
              <span>Custom wire diameters and non-standard roll widths available upon specification.</span>
              <span className="hidden md:inline text-border">•</span>
              <Link to="/AstmE2016ScopeLimitationsPage" className="font-medium text-primary hover:underline inline-flex items-center gap-1">
                <Info className="size-3.5 text-primary" aria-hidden="true" />
                <span>
                  {pick(lang, {
                  en: "ASTM E2016 Scope Limitations",
                  zh: "ASTM E2016 标准范围与局限",
                  es: "Límites del alcance de ASTM E2016",
                  de: "ASTM E2016 Anwendungsbereich & Grenzen",
                  fr: "Limites du champ d'application ASTM E2016",
                  pt: "Limitações de escopo da ASTM E2016"
                })}
                </span>
                <ArrowRight className="size-3" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>

        {/* Quality & Material Standards Bar */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="rounded-lg border border-border bg-card">
            <CardHeader className="p-5 pb-2">
              <Badge variant="outline" className="w-fit text-[13px] uppercase font-mono">
                ASTM E2016 Compliance
              </Badge>
              <CardTitle className="mf-h3 text-foreground mt-2">
                Industrial Woven Mesh Standards
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0 text-[13px] text-muted-foreground leading-relaxed">
              Precision weave consistency guarantees strict wire pitch and count per inch across standard 30m production rolls.
            </CardContent>
          </Card>

          <Card className="rounded-lg border border-border bg-card">
            <CardHeader className="p-5 pb-2">
              <Badge variant="outline" className="w-fit text-[13px] uppercase font-mono">
                ISO 9044 Verified
              </Badge>
              <CardTitle className="mf-h3 text-foreground mt-2">
                Aperture & Open Area Tolerances
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0 text-[13px] text-muted-foreground leading-relaxed">
              Optical inspection software monitors wire diameter variation and mesh opening dimensions for zero downstream blinding.
            </CardContent>
          </Card>

          <Card className="rounded-lg border border-border bg-card">
            <CardHeader className="p-5 pb-2">
              <Badge variant="outline" className="w-fit text-[13px] uppercase font-mono">
                XRF Alloy Verification
              </Badge>
              <CardTitle className="mf-h3 text-foreground mt-2">
                Metallurgy & Alloy Testing
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0 text-[13px] text-muted-foreground leading-relaxed">
              Positive material identification for 304, 304L, 316, and 316L compliance can be arranged when specified in the order.
            </CardContent>
          </Card>
        </div>

        {/* Official Technical Datasheets (CPT: technical_document) */}
        <div className="mt-16 pt-12 border-t border-border">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 mf-caption font-semibold uppercase tracking-wider text-primary mb-1">
                <FileText className="size-3.5" aria-hidden="true" />
                Technical Documentation
              </div>
              <h3 className="mf-h3 text-foreground">
                Downloadable engineering specifications & SOP sheets
              </h3>
            </div>
            <Button asChild variant="outline">
              <Link to="/DatasheetsPage">
                View All Datasheets
                <ArrowRight className="size-4 ml-1.5" aria-hidden="true" />
              </Link>
            </Button>
          </div>

          <PostsProvider wp_query={wpQuery}>
            <TechnicalDocumentsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>;
}